"use client"

import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { platformStorage } from '@/lib/platform-storage'
import { convex, whenConvexReady } from '@/lib/convex-client'
import { api } from '@/convex/_generated/api'
import { useAuthStore } from './auth-store'
import {
  sendBrowserNotification,
  requestBrowserPermission,
  getBrowserPermissionState,
  subscribeToBrowserPermission,
  type BrowserPermissionState,
} from '@/lib/notification-service'
import {
  clearNotifications,
  createNotification,
  deleteNotification as deleteRemoteNotification,
  markAllNotificationsRead,
  markNotificationRead,
  subscribeToNotifications,
} from '@/lib/convex-notifications'
import type {
  AppNotification,
  NotificationType,
  NotificationPreferences,
  NotificationSoundPreference,
} from '@/types/notification'
import {
  DEFAULT_NOTIFICATION_PREFERENCES,
  getNotificationPriority,
} from '@/types/notification'
import { toast } from 'sonner'
import { showInAppNotificationToast } from '@/lib/in-app-notification-toast'

// ── Store interface ────────────────────────────────────────────────────────────

interface NotificationState {
  // Notification center
  notifications: AppNotification[]
  unreadCount: number
  isLoading: boolean
  isOpen: boolean

  // Preferences
  preferences: NotificationPreferences

  // Browser permission
  browserPermission: BrowserPermissionState

  // Permission prompt tracking
  hasSeenPermissionPrompt: boolean

  // Deduplication: track recently sent notification tags
  _recentTags: Set<string>
}

interface NotificationActions {
  // Core actions
  notify: (options: {
    type: NotificationType
    title: string
    message: string
    taskId?: string
    tag?: string
    onClick?: () => void
  }) => Promise<void>

  // Notification center
  subscribeToInbox: () => () => void
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotification: (notificationId: string) => Promise<void>
  clearAll: () => Promise<void>
  setIsOpen: (open: boolean) => void

  // Preferences
  fetchPreferences: () => Promise<void>
  updatePreferences: (updates: Partial<NotificationPreferences>) => Promise<void>

  // Browser permission
  requestPermission: () => Promise<BrowserPermissionState>
  refreshPermissionState: () => void
  watchPermissionState: () => () => void
  markPermissionPromptAsSeen: () => void
}

type NotificationStore = NotificationState & NotificationActions

// ── Helpers ────────────────────────────────────────────────────────────────────

const DEDUP_WINDOW_MS = 30_000 // 30 seconds dedup window
const MAX_NOTIFICATIONS = 50   // Keep latest 50 in local state

/**
 * Ids the user has already been shown a toast for.
 *
 * Module scope rather than closure scope so `notify` can claim the id it just
 * wrote: the client raises its own toast for those, and the subscription
 * delivers the stored row back within milliseconds, which would otherwise toast
 * the same event twice.
 */
const seenNotificationIds = new Set<string>()

// ── Store ──────────────────────────────────────────────────────────────────────

export const useNotificationStore = create<NotificationStore>()(
  devtools(
    persist(
      (set, get) => ({
        // ── Initial state ────────────────────────────────────────────────
        notifications: [],
        unreadCount: 0,
        isLoading: false,
        isOpen: false,
        preferences: { ...DEFAULT_NOTIFICATION_PREFERENCES },
        // Read live rather than defaulted: this is browser state, not ours.
        browserPermission: getBrowserPermissionState(),
        hasSeenPermissionPrompt: false,
        _recentTags: new Set(),

        // ── Core: send a notification ────────────────────────────────────
        notify: async ({ type, title, message, taskId, tag, onClick }) => {
          const user = useAuthStore.getState().user
          if (!user) return

          const { preferences, _recentTags, browserPermission } = get()

          // Check if this notification type is enabled
          if (type === 'timer_complete' && !preferences.timer_notifications) return
          if ((type === 'pomodoro_focus_end' || type === 'pomodoro_break_end') && !preferences.pomodoro_notifications) return
          if ((type === 'task_upcoming' || type === 'task_overdue') && !preferences.task_reminder_notifications) return

          // Dedup check
          const dedupeTag = tag ?? `${type}-${taskId ?? 'no-task'}`
          if (_recentTags.has(dedupeTag)) return

          set(
            (state) => {
              const next = new Set(state._recentTags)
              next.add(dedupeTag)
              return { _recentTags: next }
            },
            false,
            'notification/dedup-add',
          )
          setTimeout(() => {
            const next = new Set(get()._recentTags)
            next.delete(dedupeTag)
            set({ _recentTags: next }, false, 'notification/dedup-expire')
          }, DEDUP_WINDOW_MS)

          // Auto-request browser permission if not yet decided
          if (browserPermission === 'default' && preferences.push_enabled) {
            const result = await requestBrowserPermission()
            set({ browserPermission: result }, false, 'notification/auto-permission')
          }

          // 1. Send browser notification (+ sound)
          if (preferences.push_enabled) {
            sendBrowserNotification({
              type,
              title,
              body: message,
              soundPreference: preferences.sound_preference,
              tag: dedupeTag,
              onClick,
            })
          }

          // 2. Show in-app toast as well
          const toastFn = type === 'task_overdue' ? toast.warning : toast.info
          toastFn(title, { description: message, duration: 6000 })

          // 3. Persist for the notification center. The inbox subscription
          //    delivers the stored row back, so nothing is inserted locally.
          try {
            const created = await createNotification({
              type,
              title,
              message,
              priority: getNotificationPriority(type),
              taskId: taskId ?? null,
            })
            seenNotificationIds.add(created.id)
          } catch (error) {
            console.error('Error persisting notification:', error)
          }
        },

        /**
         * Live inbox. Convex pushes the list on every write — including the
         * reminders the cron produces — so there is nothing to poll or
         * invalidate, and no cache to keep fresh.
         *
         * Toasting lives here rather than in the hook because only the
         * subscription can tell a genuinely new notification from the backlog
         * that arrives with the first payload.
         */
        subscribeToInbox: () => {
          set({ isLoading: get().notifications.length === 0 }, false, 'notification/subscribe')

          let primed = false

          return subscribeToNotifications((notifications) => {
            set(
              {
                notifications: notifications.slice(0, MAX_NOTIFICATIONS),
                unreadCount: notifications.filter((item) => !item.read).length,
                isLoading: false,
              },
              false,
              'notification/inbox-update',
            )

            if (!primed) {
              // The first payload is the backlog, not news.
              primed = true
              notifications.forEach((item) => seenNotificationIds.add(item.id))
              return
            }

            for (const notification of notifications) {
              if (seenNotificationIds.has(notification.id)) continue
              seenNotificationIds.add(notification.id)
              if (notification.read) continue

              showInAppNotificationToast({
                type: notification.type,
                title: notification.title,
                message: notification.message,
                dedupeKey: notification.task_id
                  ? `task-${notification.type}-${notification.task_id}`
                  : notification.id,
              })
            }
          })
        },

        // ── Mark as read ─────────────────────────────────────────────────
        markAsRead: async (notificationId: string) => {
          // Optimistic update; the subscription confirms it a moment later.
          set(
            (state) => ({
              notifications: state.notifications.map((n) =>
                n.id === notificationId
                  ? { ...n, read: true, read_at: new Date().toISOString() }
                  : n,
              ),
              unreadCount: Math.max(0, state.unreadCount - 1),
            }),
            false,
            'notification/mark-read',
          )

          try {
            await markNotificationRead(notificationId)
          } catch (error) {
            console.error('Error marking notification as read:', error)
          }
        },

        // ── Mark all as read ─────────────────────────────────────────────
        markAllAsRead: async () => {
          const user = useAuthStore.getState().user
          if (!user) return

          const now = new Date().toISOString()

          set(
            (state) => ({
              notifications: state.notifications.map((n) => ({
                ...n,
                read: true,
                read_at: n.read_at ?? now,
              })),
              unreadCount: 0,
            }),
            false,
            'notification/mark-all-read',
          )

          try {
            await markAllNotificationsRead()
          } catch (error) {
            console.error('Error marking all as read:', error)
          }
        },

        // ── Delete notification ──────────────────────────────────────────
        deleteNotification: async (notificationId: string) => {
          const notification = get().notifications.find((n) => n.id === notificationId)

          set(
            (state) => ({
              notifications: state.notifications.filter((n) => n.id !== notificationId),
              unreadCount: notification && !notification.read
                ? Math.max(0, state.unreadCount - 1)
                : state.unreadCount,
            }),
            false,
            'notification/delete',
          )

          try {
            await deleteRemoteNotification(notificationId)
          } catch (error) {
            console.error('Error deleting notification:', error)
          }
        },

        // ── Clear all notifications ──────────────────────────────────────
        clearAll: async () => {
          const user = useAuthStore.getState().user
          if (!user) return

          set({ notifications: [], unreadCount: 0 }, false, 'notification/clear-all')

          try {
            await clearNotifications()
          } catch (error) {
            console.error('Error clearing notifications:', error)
          }
        },

        setIsOpen: (open: boolean) => {
          set({ isOpen: open }, false, 'notification/toggle-panel')
        },

        // ── Preferences ──────────────────────────────────────────────────
        fetchPreferences: async () => {
          const user = useAuthStore.getState().user
          if (!user) return

          try {
            // Preferences live on the Convex user document (the schema merges
            // the old profiles + user_preferences tables).
            await whenConvexReady()
            const data = await convex.query(api.users.current, {})

            if (data) {
              set(
                {
                  preferences: {
                    email_notifications: data.emailNotifications ?? true,
                    push_enabled: data.pushNotifications ?? true,
                    sound_preference: (data.notificationSound === 'system' ? 'standard' : (data.notificationSound as NotificationSoundPreference)) ?? 'standard',
                    timer_notifications: data.timerNotifications ?? true,
                    pomodoro_notifications: data.pomodoroNotifications ?? true,
                    task_reminder_notifications: data.taskReminderNotifications ?? true,
                  },
                },
                false,
                'notification/prefs-loaded',
              )
            }
          } catch (error) {
            console.error('Error fetching notification preferences:', error)
          }
        },

        updatePreferences: async (updates: Partial<NotificationPreferences>) => {
          const user = useAuthStore.getState().user
          if (!user) return

          // Optimistic local update
          set(
            (state) => ({
              preferences: { ...state.preferences, ...updates },
            }),
            false,
            'notification/prefs-update',
          )

          try {
            const dbUpdates: Record<string, unknown> = {}
            if (updates.email_notifications !== undefined) dbUpdates.emailNotifications = updates.email_notifications
            if (updates.push_enabled !== undefined) dbUpdates.pushNotifications = updates.push_enabled
            if (updates.sound_preference !== undefined) dbUpdates.notificationSound = updates.sound_preference
            if (updates.timer_notifications !== undefined) dbUpdates.timerNotifications = updates.timer_notifications
            if (updates.pomodoro_notifications !== undefined) dbUpdates.pomodoroNotifications = updates.pomodoro_notifications
            if (updates.task_reminder_notifications !== undefined) dbUpdates.taskReminderNotifications = updates.task_reminder_notifications

            await whenConvexReady()

            await convex.mutation(api.users.setPreferences, dbUpdates)
          } catch (error) {
            console.error('Error updating notification preferences:', error)
          }
        },

        // ── Browser permission ───────────────────────────────────────────
        requestPermission: async () => {
          const result = await requestBrowserPermission()
          set({ browserPermission: result }, false, 'notification/permission')
          return result
        },

        refreshPermissionState: () => {
          const state = getBrowserPermissionState()
          set({ browserPermission: state }, false, 'notification/refresh-permission')
        },

        /**
         * Keeps the permission in step with the browser for as long as the app
         * is mounted, so granting it in browser settings and switching back
         * clears the "blocked" banner without a reload.
         */
        watchPermissionState: () =>
          subscribeToBrowserPermission((state) => {
            if (get().browserPermission === state) return
            set({ browserPermission: state }, false, 'notification/permission-changed')
          }),

        markPermissionPromptAsSeen: () => {
          set({ hasSeenPermissionPrompt: true }, false, 'notification/permission-prompt-seen')
        },
      }),
      {
        name: 'notification-store',
        // Named rather than defaulted: zustand falls back to `localStorage`,
        // which does not exist on a phone. Metro resolves the `.native`
        // sibling to AsyncStorage so this store file runs on both clients.
        storage: createJSONStorage(() => platformStorage),
        // v1 stopped persisting `browserPermission`. Dropping it from
        // `partialize` alone is not enough: rehydration merges whatever is
        // already in storage, so an old cached 'denied' would come back on
        // load and re-assert that notifications were blocked. This strips it
        // from blobs written by v0.
        version: 1,
        migrate: (persisted) => {
          if (!persisted || typeof persisted !== 'object') return persisted
          const { browserPermission: _live, ...rest } = persisted as Record<string, unknown>
          return rest
        },
        partialize: (state) => ({
          preferences: state.preferences,
          hasSeenPermissionPrompt: state.hasSeenPermissionPrompt,
          // Don't persist notifications — they arrive from the subscription
          // Don't persist _recentTags — ephemeral
          // Don't persist browserPermission — it belongs to the browser and can
          // change while the app isn't looking, so a cached copy can only go
          // stale. It survived a grant in browser settings and kept claiming
          // notifications were blocked.
        }),
      },
    ),
    { name: 'NotificationStore' },
  ),
)
